import * as Sentry from '@sentry/react';
import {cn} from '@sollapay/ui';
import {
  Button,
  FieldInput,
  FieldTextarea,
  FILE_UPLOAD_ACCEPTED_FORMATS,
  FileUpload,
  Icon,
  PopoverContent,
  Typography
} from '@sollapay/ui/components';
import {FILE_UPLOAD_MAX_SIZE_BYTES, formatFileSize} from '@sollapay/utils';
import {useCallback, useMemo, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {useTranslation} from 'react-i18next';

import {useCurrentUserIdentity} from '@/hooks';
import {logger} from '@/utils/logger.utils';
import {capturePageScreenshot} from '@/utils/screenshot.utils';

import {showReportBugFeedbackToast} from './ReportBugFeedbackToast';

import type {FC} from 'react';

const MAX_FILES = 5;

const log = logger.createLogger('ReportBugModal');

export interface ReportBugModalProps {
  onOpenChange: (open: boolean) => void;
}

export const ReportBugModal: FC<ReportBugModalProps> = ({onOpenChange}) => {
  const {t} = useTranslation('common');
  const {displayName: userDisplayName, email: userEmail} = useCurrentUserIdentity();

  const [message, setMessage] = useState('');
  const [name, setName] = useState(userDisplayName ?? '');
  const [email, setEmail] = useState(userEmail ?? '');
  const [files, setFiles] = useState<{id: string; file: File}[]>([]);
  const [messageError, setMessageError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const scrollBodyRef = useRef<HTMLDivElement>(null);

  const trimmedMessage = useMemo(() => message.trim(), [message]);
  const documents = useMemo(
    () => files.map(f => ({id: f.id, fileName: f.file.name, fileSize: f.file.size})),
    [files]
  );

  const scrollToBottom = () => {
    scrollBodyRef.current?.scrollTo({top: scrollBodyRef.current.scrollHeight, behavior: 'smooth'});
  };

  const resetState = useCallback(() => {
    setMessage('');
    setName(userDisplayName ?? '');
    setEmail(userEmail ?? '');
    setFiles([]);
    setMessageError('');
    setIsSubmitting(false);
    setIsCapturing(false);
  }, [userDisplayName, userEmail]);

  const handleClose = useCallback(() => {
    resetState();
    onOpenChange(false);
  }, [onOpenChange, resetState]);

  const handleUpload = useCallback(async (file: File) => {
    flushSync(() => setFiles(prev => [...prev, {id: crypto.randomUUID(), file}]));
    scrollToBottom();
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const handleScreenshot = useCallback(async () => {
    setIsCapturing(true);
    try {
      const file = await capturePageScreenshot();
      flushSync(() => setFiles(prev => [...prev, {id: crypto.randomUUID(), file}]));
      scrollToBottom();
      showReportBugFeedbackToast(
        'reportBug.screenshotAddedTitle',
        'reportBug.screenshotAdded',
        'success'
      );
    } catch (err) {
      log.error('Screenshot capture failed', err);
      showReportBugFeedbackToast(
        'reportBug.errorTitle',
        'reportBug.errorScreenshotFailed',
        'error'
      );
    } finally {
      setIsCapturing(false);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!trimmedMessage) {
      setMessageError(t('reportBug.errorMessageRequired'));
      return;
    }
    setMessageError('');
    setIsSubmitting(true);

    try {
      const attachments = await Promise.all(
        files.map(async ({file}) => ({
          filename: file.name,
          data: new Uint8Array(await file.arrayBuffer()),
          contentType: file.type
        }))
      );

      Sentry.captureFeedback(
        {
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          message: trimmedMessage
        },
        {attachments}
      );
      Sentry.metrics.count('feedback.submitted', 1);

      showReportBugFeedbackToast('reportBug.successTitle', 'reportBug.successMessage', 'success');
      handleClose();
    } catch (err) {
      log.error('Failed to submit feedback', err);
      showReportBugFeedbackToast('reportBug.errorTitle', 'reportBug.errorSubmitFailed', 'error');
      setIsSubmitting(false);
    }
  }, [trimmedMessage, name, email, files, t, handleClose]);

  const isPrimaryDisabled = !trimmedMessage || isSubmitting || isCapturing;
  const canAddMore = files.length < MAX_FILES;

  return (
    <PopoverContent
      side="top"
      align="end"
      sideOffset={8}
      role="dialog"
      aria-labelledby="report-bug-title"
      positionerClassName="z-60"
      className={cn(
        'z-60 w-85 flex-col rounded-2xl border border-border bg-background-surface p-0 ring-0',
        'shadow-md',
        isCapturing && 'invisible'
      )}
    >
      {/* Header */}
      <div className="flex shrink-0 items-start justify-between border-b border-border px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <Icon name="bug" className="size-4 shrink-0 text-fg-secondary" />
            <Typography id="report-bug-title" weight="semibold">
              {t('reportBug.modalTitle')}
            </Typography>
          </div>
          <Typography size="xs" color="tertiary" className="whitespace-normal">
            {t('reportBug.subtitle')}
          </Typography>
        </div>
        <Button
          variant="tertiary"
          size="icon-sm"
          icon={<Icon name="x-close" className="size-4" />}
          aria-label={t('reportBug.cancel')}
          onClick={handleClose}
          className="mt-0.5 shrink-0"
        />
      </div>

      {/* Body */}
      <div
        ref={scrollBodyRef}
        className="flex flex-col gap-4 overflow-y-auto px-4 py-4"
        style={{maxHeight: '60vh'}}
      >
        <FieldTextarea
          label={t('reportBug.messageLabel')}
          placeholder={t('reportBug.messagePlaceholder')}
          value={message}
          onChange={e => {
            setMessage(e.target.value);
            if (e.target.value.trim()) setMessageError('');
          }}
          error={messageError}
          required
          rows={3}
          resizeY
        />
        <FieldInput
          label={t('reportBug.nameLabel')}
          placeholder={t('reportBug.namePlaceholder')}
          value={name}
          onChange={e => setName(e.target.value)}
          type="text"
          autoComplete="name"
        />
        <FieldInput
          label={t('reportBug.emailLabel')}
          placeholder={t('reportBug.emailPlaceholder')}
          value={email}
          onChange={e => setEmail(e.target.value)}
          type="email"
          autoComplete="email"
          dir="ltr"
        />

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Typography size="sm" weight="medium">
              {t('reportBug.attachmentsLabel')}
            </Typography>
            <Typography size="xs" color="tertiary">
              {MAX_FILES} / {files.length}
            </Typography>
          </div>
          {canAddMore && (
            <Button
              variant="secondary"
              size="stretch"
              disabled={isCapturing}
              onClick={() => void handleScreenshot()}
              icon={<Icon name="camera" />}
              iconPosition="start"
            >
              {isCapturing ? t('reportBug.capturingScreenshot') : t('reportBug.addScreenshot')}
            </Button>
          )}
          <FileUpload
            documents={documents}
            maxFiles={MAX_FILES}
            acceptedFormats={FILE_UPLOAD_ACCEPTED_FORMATS}
            maxFileSizeBytes={FILE_UPLOAD_MAX_SIZE_BYTES}
            onUpload={handleUpload}
            onDelete={handleDelete}
            clickLabel={t('reportBug.attachmentsClickLabel')}
            dragLabel={t('reportBug.attachmentsDragLabel')}
            maxSizeLabel={`${t('reportBug.attachmentsMaxSizePrefix')} ${formatFileSize(FILE_UPLOAD_MAX_SIZE_BYTES)} · ${t('reportBug.attachmentsMaxFilesHint', {count: MAX_FILES})}`}
            fileTooLargeLabel={t('reportBug.errorFileTooLarge')}
            invalidFileTypeLabel={t('reportBug.errorInvalidFileType')}
            deleteLabel={t('reportBug.attachmentsDeleteLabel')}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center gap-2 border-t border-border px-4 py-3">
        <div className="flex min-w-0 flex-1 items-start gap-1.5 text-fg-quaternary">
          <Icon name="alert-circle" width={13} height={13} className="mt-0.5 shrink-0" />
          <Typography size="xs" className="whitespace-normal text-fg-quaternary">
            {t('reportBug.footerNote')}
          </Typography>
        </div>
        <Button
          variant="primary"
          size="stretch"
          disabled={isPrimaryDisabled}
          onClick={() => void handleSubmit()}
          icon={<Icon name="send" className="-scale-x-100" />}
          iconPosition="start"
        >
          {t('reportBug.submit')}
        </Button>
      </div>
    </PopoverContent>
  );
};
